import api from "@/lib/api";
import type { EntityAttachment } from "@/types/entity-attachment";

export type TournamentStatus =
  | "DRAFT"
  | "CANCELLED"
  | "GROUP_STAGE"
  | "ROUND_OF_16"
  | "QUARTERS"
  | "SEMIS"
  | "THIRD_PLACE"
  | "FINAL"
  | "COMPLETED";

export type MatchStage =
  | "GROUP_STAGE"
  | "ROUND_OF_16"
  | "QUARTERS"
  | "SEMIS"
  | "THIRD_PLACE"
  | "FINAL";

export interface TournamentTeamMember {
  id: string;
  userId: string;
  registrationId?: string | null;
  user?: {
    id: string;
    name?: string | null;
    email?: string | null;
    avatar?: string | null;
  };
}

export interface TournamentTeam {
  id: string;
  name: string;
  photo?: string | null;
  source?: "REGISTERED" | "FORMED";
  playersPerTeam?: number | null;
  seed: number | null;
  groupNumber: number | null;
  /** Probabilidade (0..1) de classificação, calculada no servidor. */
  classificationChance: number | null;
  teamMembers?: TournamentTeamMember[];
}

export interface TournamentMatch {
  id: string;
  stage: MatchStage;
  groupNumber: number | null;
  matchOrder: number;
  homeTeamId: string | null;
  awayTeamId: string | null;
  homeScore: number | null;
  awayScore: number | null;
  winnerTeamId: string | null;
  status: "SCHEDULED" | "PLAYED";
}

export interface TournamentOrganizerTeam {
  id: string;
  name: string;
  slug: string;
  photo?: string | null;
}

export interface Tournament {
  id: string;
  teamId: string;
  /** Código curto para /t/:code (inscrição). */
  shortCode?: string | null;
  /** Time da plataforma que organiza o torneio (respostas atuais da API). */
  organizerTeam?: TournamentOrganizerTeam;
  name: string;
  coverImage: string | null;
  attachments?: EntityAttachment[];
  sport: string;
  category: string | null;
  startsAt: string | null;
  status: TournamentStatus;
  classifyFormat: "ROUND_ROBIN" | "GROUPS";
  classifyCount: number;
  groupSize: number | null;
  hasThirdPlace: boolean;
  registrationType: "TEAM" | "INDIVIDUAL" | "BOTH";
  teamRegistrationPrice: number | string;
  individualRegistrationPrice: number | string;
  registrationOpenAt: string | null;
  registrationCloseAt: string | null;
  maxTeamRegistrations: number | null;
  maxIndividualRegistrations: number | null;
  playersPerTeam?: number | null;
  paymentTimeoutMinutes: number;
  passFeesToUser: boolean;
  venueName: string | null;
  venueAddress: string | null;
  venueNotes: string | null;
  prizeFirstPlace: number | string | null;
  prizeSecondPlace: number | string | null;
  prizeThirdPlace: number | string | null;
  createdAt: string;
  updatedAt: string;
  teams: TournamentTeam[];
  matches: TournamentMatch[];
  registrations?: unknown[];
}

export interface TournamentListItem {
  id: string;
  shortCode?: string | null;
  name: string;
  coverImage: string | null;
  sport: string;
  category: string | null;
  startsAt: string | null;
  status: TournamentStatus;
  classifyFormat: "ROUND_ROBIN" | "GROUPS";
  classifyCount: number;
  groupSize: number | null;
  hasThirdPlace: boolean;
  registrationType: "TEAM" | "INDIVIDUAL" | "BOTH";
  teamRegistrationPrice: number | string;
  individualRegistrationPrice: number | string;
  registrationOpenAt: string | null;
  registrationCloseAt: string | null;
  maxTeamRegistrations: number | null;
  maxIndividualRegistrations: number | null;
  playersPerTeam?: number | null;
  paymentTimeoutMinutes: number;
  passFeesToUser: boolean;
  venueName: string | null;
  venueAddress: string | null;
  venueNotes: string | null;
  prizeFirstPlace: number | string | null;
  prizeSecondPlace: number | string | null;
  prizeThirdPlace: number | string | null;
  teamsCount: number;
  matchesCount: number;
  registrationsCount: number;
  /** Presente na listagem pública (home). */
  organizerTeam?: TournamentOrganizerTeam | null;
  createdAt: string;
  updatedAt: string;
}

export interface StandingsRow {
  teamId: string;
  name: string;
  groupNumber: number | null;
  played: number;
  wins: number;
  draws: number;
  losses: number;
  gf: number;
  ga: number;
  goalDiff: number;
  points: number;
  seed: number;
}

export interface GetPublicTournamentsParams {
  sport?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
}

export interface CreateTournamentInput {
  name: string;
  sport: string;
  category?: string | null;
  startsAt: string;
  classifyFormat?: "ROUND_ROBIN" | "GROUPS";
  classifyCount?: number;
  groupSize?: number;
  hasThirdPlace?: boolean;
  registrationType?: "TEAM" | "INDIVIDUAL" | "BOTH";
  teamRegistrationPrice?: number;
  individualRegistrationPrice?: number;
  registrationOpenAt?: string | null;
  registrationCloseAt?: string | null;
  maxTeamRegistrations?: number | null;
  maxIndividualRegistrations?: number | null;
  playersPerTeam?: number | null;
  paymentTimeoutMinutes?: number;
  passFeesToUser?: boolean;
  venueName?: string | null;
  venueAddress?: string | null;
  venueNotes?: string | null;
  prizeFirstPlace?: number | null;
  prizeSecondPlace?: number | null;
  prizeThirdPlace?: number | null;
}

export const tournamentService = {
  /** Torneios para a home; `registrationsCount` conta apenas inscrições CONFIRMED. */
  async findPublic(filters?: GetPublicTournamentsParams): Promise<TournamentListItem[]> {
    const searchParams = new URLSearchParams();
    if (filters?.sport) searchParams.set("sport", filters.sport);
    if (filters?.startDate) searchParams.set("startDate", filters.startDate);
    if (filters?.endDate) searchParams.set("endDate", filters.endDate);
    if (filters?.search?.trim()) searchParams.set("search", filters.search.trim());
    const qs = searchParams.toString();
    const url = qs ? `/tournaments/public?${qs}` : "/tournaments/public";
    const r = await api.get<TournamentListItem[]>(url);
    return r.data;
  },

  async list(teamId: string): Promise<TournamentListItem[]> {
    const r = await api.get<TournamentListItem[]>(`/teams/${teamId}/tournaments`);
    return r.data;
  },

  async create(teamId: string, data: CreateTournamentInput): Promise<Tournament> {
    const r = await api.post<Tournament>(`/teams/${teamId}/tournaments`, data);
    return r.data;
  },

  async uploadCoverImage(teamId: string, tournamentId: string, file: File): Promise<Tournament> {
    const formData = new FormData();
    formData.append("coverImage", file);
    const r = await api.post<Tournament>(
      `/teams/${teamId}/tournaments/${tournamentId}/cover-image`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    return r.data;
  },

  async uploadAttachment(
    teamId: string,
    tournamentId: string,
    file: File,
    label?: string,
  ): Promise<Tournament> {
    const formData = new FormData();
    formData.append("file", file);
    if (label?.trim()) formData.append("label", label.trim());
    const r = await api.post<Tournament>(
      `/teams/${teamId}/tournaments/${tournamentId}/attachments`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    return r.data;
  },

  async removeAttachment(
    teamId: string,
    tournamentId: string,
    attachmentId: string,
  ): Promise<Tournament> {
    const r = await api.delete<Tournament>(
      `/teams/${teamId}/tournaments/${tournamentId}/attachments/${attachmentId}`,
    );
    return r.data;
  },

  async findOne(teamId: string, id: string): Promise<Tournament> {
    const r = await api.get<Tournament>(`/teams/${teamId}/tournaments/${id}`);
    return r.data;
  },

  async update(
    teamId: string,
    id: string,
    data: Partial<CreateTournamentInput>,
  ): Promise<Tournament> {
    const r = await api.patch<Tournament>(`/teams/${teamId}/tournaments/${id}`, data);
    return r.data;
  },

  async remove(teamId: string, id: string): Promise<void> {
    await api.delete(`/teams/${teamId}/tournaments/${id}`);
  },

  async cancel(teamId: string, id: string): Promise<Tournament> {
    const r = await api.post<Tournament>(`/teams/${teamId}/tournaments/${id}/cancel`);
    return r.data;
  },

  async addTeam(teamId: string, id: string, name: string): Promise<Tournament> {
    const r = await api.post<Tournament>(`/teams/${teamId}/tournaments/${id}/teams`, {
      name,
    });
    return r.data;
  },

  async addTeamsBulk(teamId: string, id: string, names: string[]): Promise<Tournament> {
    const r = await api.post<Tournament>(
      `/teams/${teamId}/tournaments/${id}/teams/bulk`,
      { names },
    );
    return r.data;
  },

  async removeTeam(
    teamId: string,
    id: string,
    participantId: string,
  ): Promise<Tournament> {
    const r = await api.delete<Tournament>(
      `/teams/${teamId}/tournaments/${id}/teams/${participantId}`,
    );
    return r.data;
  },

  async shuffle(teamId: string, id: string): Promise<Tournament> {
    const r = await api.post<Tournament>(`/teams/${teamId}/tournaments/${id}/shuffle`);
    return r.data;
  },

  async formTeamsFromIndividuals(
    teamId: string,
    id: string,
    data: { playersPerTeam: number; strategy?: "AUTO" | "STRONG_WEAK_SPLIT" | "GENDER_SPLIT" },
  ): Promise<Tournament> {
    const r = await api.post<Tournament>(
      `/teams/${teamId}/tournaments/${id}/form-teams-from-individuals`,
      data,
    );
    return r.data;
  },

  async startGroupStage(teamId: string, id: string): Promise<Tournament> {
    const r = await api.post<Tournament>(
      `/teams/${teamId}/tournaments/${id}/start-group-stage`,
    );
    return r.data;
  },

  async startKnockout(teamId: string, id: string): Promise<Tournament> {
    const r = await api.post<Tournament>(
      `/teams/${teamId}/tournaments/${id}/start-knockout`,
    );
    return r.data;
  },

  async advance(teamId: string, id: string): Promise<Tournament> {
    const r = await api.post<Tournament>(`/teams/${teamId}/tournaments/${id}/advance`);
    return r.data;
  },

  async standings(teamId: string, id: string): Promise<StandingsRow[]> {
    const r = await api.get<StandingsRow[]>(
      `/teams/${teamId}/tournaments/${id}/standings`,
    );
    return r.data;
  },

  async updateMatch(
    teamId: string,
    id: string,
    matchId: string,
    data: {
      homeScore: number;
      awayScore: number;
      winnerTeamId?: string | null;
    },
  ): Promise<Tournament> {
    const r = await api.patch<Tournament>(
      `/teams/${teamId}/tournaments/${id}/matches/${matchId}`,
      data,
    );
    return r.data;
  },
};
